import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import {
  ArrowRight,
  Shield,
  BarChart3,
  FileText,
  Users,
  TrendingUp,
  CheckCircle2,
  Zap,
  Landmark,
  Home,
  Construction,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Button } from '@/Components/ui/button';

// Efeito de digitação para o texto da seção "O que é" — bem mais devagar que o do
// hero (texto maior), um parágrafo de cada vez (o segundo só começa quando o
// primeiro termina, via onComplete) e com o cursor piscando enquanto digita.
// Dispara só quando a seção entra na tela pela primeira vez (`start` vem de
// onViewportEnter com viewport once:true, nunca reinicia ao rolar pra fora e
// voltar). Ficam fora do componente da página para não recriar a cada re-render.
const SECTION_TYPE_SPEED_MS = 18;
function TypeCaret() {
  return <span className="inline-block w-[2px] sm:w-[3px] h-[1em] align-middle bg-[#0074f6] ml-0.5 animate-pulse" />;
}
function SectionTypewriter({ text, start, prefersReducedMotion, onComplete }) {
  const [typedCount, setTypedCount] = useState(prefersReducedMotion ? text.length : 0);
  const completedRef = useRef(false);
  useEffect(() => {
    if (completedRef.current) return undefined;
    if (prefersReducedMotion || (start && typedCount >= text.length)) {
      completedRef.current = true;
      onComplete && onComplete();
      return undefined;
    }
    if (!start) return undefined;
    const t = setTimeout(() => setTypedCount((c) => c + 1), SECTION_TYPE_SPEED_MS);
    return () => clearTimeout(t);
  }, [start, typedCount, text.length, prefersReducedMotion]);
  const showCaret = start && !prefersReducedMotion && typedCount < text.length;
  return (
    <>
      {text.slice(0, typedCount)}
      {showCaret && <TypeCaret />}
    </>
  );
}

// Versão com um trecho "rico" no meio (o link da Consulta Pública): digita cada
// parte em sequência como texto puro e só troca pelo elemento de verdade (botão
// clicável) quando aquele trecho termina de ser digitado.
function SectionTypewriterRich({ parts, start, prefersReducedMotion }) {
  const totalLength = parts.reduce((sum, p) => sum + p.text.length, 0);
  const [typedCount, setTypedCount] = useState(prefersReducedMotion ? totalLength : 0);
  useEffect(() => {
    if (prefersReducedMotion || !start || typedCount >= totalLength) return undefined;
    const t = setTimeout(() => setTypedCount((c) => c + 1), SECTION_TYPE_SPEED_MS);
    return () => clearTimeout(t);
  }, [start, typedCount, totalLength, prefersReducedMotion]);

  let consumed = 0;
  const showCaret = start && !prefersReducedMotion && typedCount < totalLength;
  return (
    <>
      {parts.map((part, i) => {
        const partStart = consumed;
        consumed += part.text.length;
        const visibleChars = Math.max(0, Math.min(part.text.length, typedCount - partStart));
        if (part.render) {
          return visibleChars >= part.text.length
            ? <React.Fragment key={i}>{part.render(part.text)}</React.Fragment>
            : <span key={i}>{part.text.slice(0, visibleChars)}</span>;
        }
        return <span key={i}>{part.text.slice(0, visibleChars)}</span>;
      })}
      {showCaret && <TypeCaret />}
    </>
  );
}

// Revelação "giro" linha a linha, amarrada à direção da rolagem — descendo, cada
// linha gira pra dentro (como uma aba abrindo) revelando o conteúdo; subindo, o
// mesmo giro acontece ao contrário e o conteúdo some, como se nunca tivesse
// existido. viewport once:false: reage de novo toda vez que entra/sai da tela,
// independente das outras seções da página (cada linha tem seu próprio observer).
function ScrollFlipReveal({ children, scrollDirection, delay = 0, prefersReducedMotion, className }) {
  const hiddenRotate = scrollDirection === 'up' ? -78 : 78;
  return (
    <motion.div
      initial={prefersReducedMotion ? false : { opacity: 0, rotateX: hiddenRotate }}
      whileInView={{ opacity: 1, rotateX: 0 }}
      viewport={{ once: prefersReducedMotion, amount: 0.5 }}
      transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
      style={{ transformPerspective: 900, transformOrigin: 'top center' }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export default function Sobre() {
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();
  const carouselRef = useRef(null);
  const isPausedRef = useRef(false);
  const resumeTimeoutRef = useRef(null);
  const [whatIsTypeStarted, setWhatIsTypeStarted] = useState(false);
  const [whatIsPara1Done, setWhatIsPara1Done] = useState(false);
  const [scrollDirection, setScrollDirection] = useState('down');

  // Direção da rolagem — usada pelos itens de "O que o sistema garante": descendo,
  // eles entram vindo da esquerda; subindo, saem pela direita (viewport once:false,
  // então o item reage de novo toda vez que entra/sai da tela).
  useEffect(() => {
    let lastY = window.scrollY;
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        if (Math.abs(y - lastY) > 4) {
          setScrollDirection(y > lastY ? 'down' : 'up');
          lastY = y;
        }
        ticking = false;
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Rolagem automática lenta (direita → esquerda). Pausa em hover/toque/clique
  // manual e retoma sozinha depois de um tempo. Lista duplicada uma vez para o
  // loop dar a volta sem "salto" visível.
  useEffect(() => {
    if (prefersReducedMotion) return undefined;
    const el = carouselRef.current;
    if (!el) return undefined;
    let rafId;
    // scrollLeft só existe como inteiro no DOM — se somarmos 0.4 nele direto,
    // a fração se perde a cada leitura e o carrossel nunca sai do lugar.
    // Por isso a posição real fica numa variável própria (fracionária) e só
    // o valor arredondado é escrito no elemento.
    let pos = el.scrollLeft;
    const step = () => {
      if (!isPausedRef.current && el.scrollWidth > el.clientWidth) {
        pos += 0.4;
        const singleSetWidth = el.scrollWidth / 2;
        if (pos >= singleSetWidth) pos -= singleSetWidth;
        el.scrollLeft = pos;
      } else {
        pos = el.scrollLeft;
      }
      rafId = requestAnimationFrame(step);
    };
    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, [prefersReducedMotion]);

  const pauseThenResume = () => {
    isPausedRef.current = true;
    clearTimeout(resumeTimeoutRef.current);
    resumeTimeoutRef.current = setTimeout(() => {
      isPausedRef.current = false;
    }, 3500);
  };

  const scrollCarousel = (direction) => {
    const el = carouselRef.current;
    if (!el) return;
    const card = el.querySelector('[data-carousel-card]');
    const gap = 24;
    const step = (card ? card.getBoundingClientRect().width : 320) + gap;
    pauseThenResume();
    el.scrollBy({ left: direction * step, behavior: 'smooth' });
  };

  const capabilities = [
    {
      icon: FileText,
      title: 'Gestão de parcerias',
      description: 'Cadastro completo, importação em lote via planilha, cálculo automático de status e prazos, exportação em Excel, CSV e PDF.'
    },
    {
      icon: BarChart3,
      title: 'Relatórios e análises',
      description: 'Dashboard com estatísticas em tempo real, gráficos interativos e relatórios por organização, com declarações em PDF geradas automaticamente.'
    },
    {
      icon: Shield,
      title: 'Auditoria completa',
      description: 'Registro de toda ação relevante do sistema — quem fez o quê, quando e de onde —, histórico de login e de alterações campo a campo.'
    },
    {
      icon: Users,
      title: 'Controle de acesso',
      description: 'Papéis hierárquicos distintos, bloqueio de conta por tentativas de login malsucedidas e sessão com renovação automática.'
    },
    {
      icon: TrendingUp,
      title: 'Monitoramento contínuo',
      description: 'Identificação rápida de parcerias em débito ou fora do prazo, com alertas e notificações automáticas.'
    },
    {
      icon: Zap,
      title: 'Importação inteligente',
      description: 'Upload de planilhas com validação linha a linha e detecção automática de duplicatas, evitando cadastro repetido.'
    }
  ];

  const benefits = [
    'Monitoramento contínuo e efetivo das parcerias com OSCs',
    'Controle rigoroso de prazos, valores e status de convênios',
    'Gestão eficiente das relações entre prefeitura e organizações',
    'Prevenção e identificação rápida de inadimplências',
    'Trilha de auditoria completa, cobrindo toda ação relevante',
    'Conformidade com legislação e normas de fiscalização',
    'Tomada de decisão baseada em dados confiáveis e atualizados',
    'Consulta pública independente de Emendas Impositivas, sem necessidade de login'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-slate-50 text-slate-900">
      {/* Header/Navbar */}
      <nav className="sticky top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg shadow-sm border-b border-[#003098]/10">
        <div className="container mx-auto px-3 sm:px-6 py-2 sm:py-3">
          <div className="flex items-center justify-between gap-2">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1 text-left"
            >
              <img
                src="/brazao_logo.png"
                alt="Prefeitura de Corumbá"
                className="h-10 sm:h-12 md:h-14 w-auto flex-shrink-0"
              />
              <div className="border-l-2 border-[#003098] pl-2 sm:pl-4 min-w-0">
                <h1 className="text-xs sm:text-sm md:text-lg font-bold text-[#003098] truncate">
                  Controle e Monitoramento
                </h1>
                <p className="text-[10px] sm:text-xs text-[#0074f6] truncate">Parcerias & OSC</p>
              </div>
            </button>

            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="border-2 border-[#003098] text-[#003098] hover:bg-[#003098] hover:text-white text-xs sm:text-sm h-8 sm:h-9 md:h-10 px-3 sm:px-4 whitespace-nowrap font-semibold"
              >
                <Home className="mr-1 sm:mr-2 w-3 h-3 sm:w-4 sm:h-4" />
                Início
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-10 sm:py-14 md:py-16">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 sm:gap-3 px-3 sm:px-6 py-2 sm:py-3 bg-white/80 backdrop-blur-sm border-2 border-[#003098]/20 rounded-full shadow-lg mb-6">
              <Landmark className="w-4 h-4 sm:w-5 sm:h-5 text-[#003098] flex-shrink-0" />
              <span className="text-xs sm:text-sm font-semibold text-[#003098] whitespace-nowrap">
                Prefeitura Municipal de Corumbá/MS
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 text-[#003098]">
              Sobre o sistema
            </h1>
            <p className="text-sm sm:text-base md:text-lg text-slate-700 leading-relaxed">
              O que é o Sistema de Controle e Monitoramento de Parcerias & OSC, por que ele existe e o que ele entrega no dia a dia da gestão pública municipal.
            </p>
          </motion.div>
        </div>
      </section>

      {/* O que é */}
      <section className="py-12 sm:py-16 bg-white/60">
        <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            onViewportEnter={() => setWhatIsTypeStarted(true)}
          >
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 text-[#003098]">
              O que é
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-slate-700 leading-relaxed mb-4">
              <SectionTypewriter
                text="Sistema web desenvolvido para centralizar o acompanhamento e a auditoria de parcerias com Organizações da Sociedade Civil (OSCs) — do cadastro inicial básico até o encerramento do monitoramento, passando pelo controle automático de prazos e status. Substitui o controle manual por planilhas por um sistema único, com cálculo automático de status (evitando erro humano e desatualização) e uma trilha de auditoria que cobre toda ação relevante."
                start={whatIsTypeStarted}
                prefersReducedMotion={prefersReducedMotion}
                onComplete={() => setWhatIsPara1Done(true)}
              />
            </p>
            <p className="text-sm sm:text-base md:text-lg text-slate-700 leading-relaxed">
              <SectionTypewriterRich
                start={whatIsPara1Done}
                prefersReducedMotion={prefersReducedMotion}
                parts={[
                  { text: 'Inclui também um módulo público independente — a ' },
                  {
                    text: 'Consulta Pública de Emendas Impositivas',
                    render: (label) => (
                      <button
                        onClick={() => navigate('/emendas')}
                        className="text-[#003098] font-semibold underline hover:text-[#0074f6]"
                      >
                        {label}
                      </button>
                    ),
                  },
                  { text: ' —, sem necessidade de login, para consulta de emendas parlamentares municipais e estaduais por qualquer cidadão.' },
                ]}
              />
            </p>
          </motion.div>
        </div>
      </section>

      {/* Por que existe — cada bloco gira pra revelar ao descer e desfaz o giro ao
          subir (ScrollFlipReveal); a seção não tem mais uma animação própria no
          contêiner pai, pra não competir com o giro linha a linha de cada filho. */}
      <section className="py-12 sm:py-16">
        <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
          <ScrollFlipReveal scrollDirection={scrollDirection} prefersReducedMotion={prefersReducedMotion} className="inline-block mb-4">
            <span className="px-4 py-2 bg-[#003098]/10 text-[#003098] rounded-full text-sm font-bold">
              Nosso Propósito
            </span>
          </ScrollFlipReveal>

          <ScrollFlipReveal scrollDirection={scrollDirection} prefersReducedMotion={prefersReducedMotion} delay={0.05}>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 text-[#003098]">
              Por que produzimos este sistema?
            </h2>
          </ScrollFlipReveal>

          <ScrollFlipReveal scrollDirection={scrollDirection} prefersReducedMotion={prefersReducedMotion} delay={0.1}>
            <p className="text-sm sm:text-base md:text-lg text-slate-700 mb-6 font-medium leading-relaxed">
              Desenvolvemos esta plataforma com o <span className="text-[#003098] font-bold">objetivo específico de estabelecer um monitoramento e controle efetivo das Organizações da Sociedade Civil (OSCs)</span>, garantindo eficiência e conformidade na gestão de parcerias municipais.
            </p>
          </ScrollFlipReveal>

          <ScrollFlipReveal scrollDirection={scrollDirection} prefersReducedMotion={prefersReducedMotion} delay={0.15} className="mb-6">
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-500 p-4 rounded-r-lg">
              <div className="flex items-start gap-3">
                <Construction className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-900 font-bold text-sm mb-1">🚧 Funcionalidade em Desenvolvimento</p>
                  <p className="text-amber-800 text-sm">
                    Estamos trabalhando na integração com o sistema de controladoria para disponibilizar consulta pública de todas as OSCs cadastradas, permitindo acesso transparente às informações das organizações parceiras.
                  </p>
                </div>
              </div>
            </div>
          </ScrollFlipReveal>

          <ScrollFlipReveal scrollDirection={scrollDirection} prefersReducedMotion={prefersReducedMotion} delay={0.2} className="mb-8">
            <div className="bg-gradient-to-r from-[#003098]/5 to-[#0074f6]/5 border-l-4 border-[#003098] p-4 rounded-r-lg">
              <p className="text-slate-700 font-medium italic">
                "Um sistema criado pela gestão pública municipal para atender às necessidades de fiscalização, controle e gestão eficiente no relacionamento com as OSCs."
              </p>
            </div>
          </ScrollFlipReveal>

          <ScrollFlipReveal scrollDirection={scrollDirection} prefersReducedMotion={prefersReducedMotion} delay={0.25}>
            <h3 className="text-lg sm:text-xl font-bold mb-4 text-[#003098]">O que o sistema garante</h3>
          </ScrollFlipReveal>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
            {benefits.map((benefit, index) => (
              <ScrollFlipReveal
                key={index}
                scrollDirection={scrollDirection}
                prefersReducedMotion={prefersReducedMotion}
                delay={0.3 + index * 0.04}
                className="flex items-start space-x-3"
              >
                <CheckCircle2 className="w-5 h-5 text-[#0074f6] flex-shrink-0 mt-0.5" />
                <span className="text-slate-700 font-medium text-sm sm:text-base">{benefit}</span>
              </ScrollFlipReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Como funciona */}
      <section className="py-12 sm:py-16 bg-white/60">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 text-[#003098]">
              O que o sistema faz, na prática
            </h2>
            <p className="text-sm sm:text-base text-slate-600 max-w-2xl mx-auto">
              As seis frentes que sustentam o dia a dia da gestão de parcerias
            </p>
          </motion.div>

          <div className="relative max-w-6xl mx-auto">
            <div
              ref={carouselRef}
              onMouseEnter={() => { isPausedRef.current = true; }}
              onMouseLeave={() => { isPausedRef.current = false; }}
              onPointerDown={() => { isPausedRef.current = true; }}
              onPointerUp={pauseThenResume}
              onPointerCancel={pauseThenResume}
              className="flex gap-4 sm:gap-6 overflow-x-auto pb-2 [&::-webkit-scrollbar]:hidden"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {[...capabilities, ...capabilities].map((cap, index) => (
                <div
                  key={index}
                  data-carousel-card
                  className="shrink-0 w-[85%] sm:w-[46%] lg:w-[31.5%] bg-white border-2 border-[#003098]/10 rounded-2xl p-5 sm:p-6 hover:border-[#003098]/30 hover:shadow-lg transition-all"
                >
                  <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-[#003098] to-[#0074f6] rounded-xl mb-3 sm:mb-4 shadow-lg">
                    <cap.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <h3 className="text-base sm:text-lg font-bold mb-2 text-[#003098]">{cap.title}</h3>
                  <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">{cap.description}</p>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-center gap-3 mt-6">
              <button
                type="button"
                onClick={() => scrollCarousel(-1)}
                aria-label="Card anterior"
                className="w-10 h-10 rounded-full border-2 border-[#003098]/20 flex items-center justify-center text-[#003098] hover:bg-[#003098] hover:text-white hover:border-[#003098] transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={() => scrollCarousel(1)}
                aria-label="Próximo card"
                className="w-10 h-10 rounded-full border-2 border-[#003098]/20 flex items-center justify-center text-[#003098] hover:bg-[#003098] hover:text-white hover:border-[#003098] transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Quem desenvolveu */}
      <section className="py-12 sm:py-16">
        <div className="container mx-auto px-4 sm:px-6 max-w-3xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 text-[#003098]">
              Quem desenvolveu
            </h2>
            <p className="text-sm sm:text-base text-slate-700 leading-relaxed mb-6">
              Sistema desenvolvido pelo Núcleo de Gestão Estratégica da Secretaria de Planejamento (SEPRAD), da Prefeitura Municipal de Corumbá/MS, com o apoio da Controladoria-Geral e da Secretaria Municipal de Gestão (SEGES), que impulsionaram a ideia e as melhorias do sistema.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <div className="flex items-center gap-2 bg-gradient-to-r from-[#0074f6]/5 to-[#003098]/5 px-4 py-2 rounded-lg border border-[#0074f6]/10">
                <div className="w-1.5 h-1.5 bg-[#0074f6] rounded-full"></div>
                <span className="font-bold text-[#0074f6] text-xs sm:text-sm whitespace-nowrap">Núcleo de Gestão Estratégica/SEPRAD</span>
              </div>
              <div className="flex items-center gap-2 bg-gradient-to-r from-[#003098]/5 to-[#0074f6]/5 px-4 py-2 rounded-lg border border-[#003098]/10">
                <div className="w-1.5 h-1.5 bg-[#003098] rounded-full"></div>
                <span className="font-bold text-[#003098] text-xs sm:text-sm whitespace-nowrap">Controladoria-Geral/SEGES</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 sm:py-20 relative bg-gradient-to-br from-[#003098] to-[#0074f6]">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-[0.05]" />
        <div className="container mx-auto px-4 sm:px-6 relative z-10 text-center">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 sm:mb-6 text-white px-4">
            Pronto para acessar o sistema?
          </h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <Button
              size="lg"
              onClick={() => navigate('/login')}
              className="bg-white text-[#003098] hover:bg-blue-50 px-6 sm:px-8 py-4 sm:py-6 text-sm sm:text-base shadow-2xl transition-all font-bold w-full sm:w-auto"
            >
              Acessar Plataforma
              <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/emendas')}
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#003098] px-6 sm:px-8 py-4 sm:py-6 text-sm sm:text-base font-semibold transition-all w-full sm:w-auto"
            >
              Consulta Pública de Emendas
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-white border-t-2 border-[#003098]/10">
        <div className="container mx-auto px-6">
          <div className="flex flex-col items-center justify-center gap-1">
            <div className="text-slate-600 text-xs font-medium whitespace-nowrap">
              © {new Date().getFullYear()} Prefeitura Municipal de Corumbá/MS — Todos os direitos reservados
            </div>
            <div className="text-slate-400 text-[11px] font-medium whitespace-nowrap">
              Sistema de Parcerias v5.0
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
